import { useMemo } from "react";
import type { GitStatusResult } from "@cmmd-forge/contracts";

import {
  buildMenuItems,
  resolveDefaultBranchActionDialogCopy,
  resolveQuickAction,
  type DefaultBranchActionDialogCopy,
  type GitActionMenuItem,
  type GitQuickAction,
} from "./GitActionsControl.logic";
import type { PendingDefaultBranchAction } from "./GitActionsControl.runner";

type GitChangedFile = GitStatusResult["workingTree"]["files"][number];

export interface GitActionsControlModel {
  readonly isRepo: boolean;
  readonly hasOriginRemote: boolean;
  readonly allFiles: readonly GitChangedFile[];
  readonly selectedFiles: readonly GitChangedFile[];
  readonly allSelected: boolean;
  readonly noneSelected: boolean;
  readonly isDefaultBranch: boolean;
  readonly gitActionMenuItems: readonly GitActionMenuItem[];
  readonly quickAction: GitQuickAction;
  readonly quickActionDisabledReason: string | null;
  readonly pendingDefaultBranchActionCopy: DefaultBranchActionDialogCopy | null;
}

export function useGitActionsControlModel(input: {
  readonly gitStatus: GitStatusResult | null;
  readonly excludedFiles: ReadonlySet<string>;
  readonly isGitActionRunning: boolean;
  readonly pendingDefaultBranchAction: PendingDefaultBranchAction | null;
}): GitActionsControlModel {
  const isRepo = input.gitStatus?.isRepo ?? true;
  const hasOriginRemote = input.gitStatus?.hasOriginRemote ?? false;
  const allFiles = input.gitStatus?.workingTree.files ?? [];
  const selectedFiles = allFiles.filter((file) => !input.excludedFiles.has(file.path));
  const allSelected = input.excludedFiles.size === 0;
  const noneSelected = selectedFiles.length === 0;
  const isDefaultBranch = input.gitStatus?.isDefaultBranch ?? false;

  const gitActionMenuItems = useMemo(
    () => buildMenuItems(input.gitStatus, input.isGitActionRunning, hasOriginRemote),
    [input.gitStatus, hasOriginRemote, input.isGitActionRunning],
  );
  const quickAction = useMemo(
    () =>
      resolveQuickAction(
        input.gitStatus,
        input.isGitActionRunning,
        isDefaultBranch,
        hasOriginRemote,
      ),
    [input.gitStatus, hasOriginRemote, isDefaultBranch, input.isGitActionRunning],
  );
  const pendingDefaultBranchActionCopy = input.pendingDefaultBranchAction
    ? resolveDefaultBranchActionDialogCopy({
        action: input.pendingDefaultBranchAction.action,
        branchName: input.pendingDefaultBranchAction.branchName,
        includesCommit: input.pendingDefaultBranchAction.includesCommit,
      })
    : null;

  return {
    isRepo,
    hasOriginRemote,
    allFiles,
    selectedFiles,
    allSelected,
    noneSelected,
    isDefaultBranch,
    gitActionMenuItems,
    quickAction,
    quickActionDisabledReason: quickAction.disabled
      ? (quickAction.hint ?? "This action is currently unavailable.")
      : null,
    pendingDefaultBranchActionCopy,
  };
}
